/**
 * The Sessions columns, declared once (design-spec §4 "Column header", D36).
 *
 * The header and every row render from this list onto one grid (`RowsGrid` in the design
 * system), so a column cannot sit in one place in the header and another in the rows. The
 * widths are the grid's, not this list's: `minmax(0,1fr) 128px 134px 76px 98px 146px`.
 *
 * The keys are the cells' `data-column` values, kept from the old table so the specs that
 * address a cell by key (`title`, `when`, `duration`) did not move. Shape and notes are gone:
 * the row carries title, person, date, length and state only (D28).
 */
export interface Column {
  readonly key: "title" | "person" | "when" | "duration" | "summary" | "expiry";
  readonly label: string;
  /** Started and Length are right-aligned tabular figures. */
  readonly align?: "right";
  /** Summary takes 12px extra left inset, in the header and the rows alike. */
  readonly inset?: boolean;
}

export const COLUMNS: readonly Column[] = [
  { key: "title", label: "Session" },
  { key: "person", label: "Person" },
  { key: "when", label: "Started", align: "right" },
  { key: "duration", label: "Length", align: "right" },
  { key: "summary", label: "Summary", inset: true },
  // Issue 193 renames the header; the cell is today's expiry chip until 195.
  { key: "expiry", label: "Video" },
] as const;

/** A column by key, for the cells that render outside the row's loop. */
export function column(key: Column["key"]): Column {
  const found = COLUMNS.find((each) => each.key === key);
  if (found === undefined) throw new Error(`no ${key} column`);
  return found;
}
